import React, { ComponentType, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import { EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { LoadingSpinner } from "@mapmycustomers/ui";

import consts from "@app/component/preview/styles/_consts.module.scss";
import ResizableDrawer from "@app/component/ResizableDrawer";
import localSettings from "@app/config/LocalSettings";
import { EnterPinLocationModePayload } from "@app/scene/map/store/pinLocation/actions";
import { RootState } from "@app/store/rootReducer";
import { EntityWithLocationProps } from "@app/types/map/EntityWithLocationProps";
import getPageContentContainer from "@app/util/ui/getPageContentContainer";
import useHiddenChat from "@app/util/useHiddenChat";

import Title from "../components/Title";

import Content from "./Content";
import styles from "./PersonRecordPane.module.scss";
import { changeActivitiesFilter, fetchPreviewData, setHasChangesFlag } from "./store/actions";
import PersonRecordData from "./store/PersonRecordData";
import { getRecordData, isLoading } from "./store/selectors";

export interface PersonRecordPaneProps {
  className?: string;
  isMapMode?: boolean;
  locationEditComponent?: ComponentType<EntityWithLocationProps>;
  onChange?: (hasChanges: boolean) => void;
  onChangePersonColor?: (color: Person["color"]) => void;
  onChangePersonShape?: (color: Person["shape"]) => void;
  onEnterPinLocationMode?: (payload: EnterPinLocationModePayload) => void;
  onHide: () => void;
  onShouldShowPrompt: (shouldShow: boolean) => void;
  personId: Person["id"];
  zIndex?: number;
}

interface Props extends PersonRecordPaneProps {
  changeActivitiesFilter: typeof changeActivitiesFilter;
  fetchPreviewData: typeof fetchPreviewData.request;
  loading: boolean;
  recordData: PersonRecordData;
  setHasChangesFlag: typeof setHasChangesFlag;
}

const PersonRecordPane: React.FC<Props> = ({
  changeActivitiesFilter,
  className,
  fetchPreviewData,
  isMapMode,
  loading,
  locationEditComponent,
  onChangePersonColor,
  onChangePersonShape,
  onEnterPinLocationMode,
  onHide,
  onShouldShowPrompt,
  personId,
  recordData,
  setHasChangesFlag,
  zIndex,
}) => {
  useHiddenChat();

  useEffect(() => {
    return () => {
      changeActivitiesFilter?.(ActivityStatusOption.ALL);
    };
  }, [changeActivitiesFilter]);

  const defaultRecordPaneWidth = localSettings.getRecordPaneWidth();
  const [detailsFormChanged, setDetailsFormChanged] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);
  const isAnythingChanged = detailsFormChanged || notesChanged;
  const { person } = recordData;
  const handleOnResize = useCallback((width) => localSettings.setRecordPaneWidth(width), []);

  useEffect(() => {
    onShouldShowPrompt(isAnythingChanged);
    setHasChangesFlag(isAnythingChanged);
  }, [isAnythingChanged, onShouldShowPrompt, setHasChangesFlag]);

  useEffect(() => {
    fetchPreviewData(personId);
    setDetailsFormChanged(false);
  }, [personId, fetchPreviewData, setDetailsFormChanged]);

  useEffect(() => {
    if (person?.color === undefined) {
      return;
    }
    onChangePersonColor?.(person.color);
  }, [person?.color, onChangePersonColor]);

  useEffect(() => {
    if (person?.shape === undefined) {
      return;
    }
    onChangePersonShape?.(person.shape);
  }, [person?.shape, onChangePersonShape]);

  return (
    <ResizableDrawer
      className={cn(styles.container, className)}
      closable
      defaultWidth={defaultRecordPaneWidth}
      disableResize={isMapMode}
      getContainer={zIndex || isMapMode ? false : getPageContentContainer()}
      mask={false}
      maxWidth={parseInt(consts.maxPaneWidth, 10)}
      onClose={onHide}
      onResize={handleOnResize}
      placement="right"
      title={person ? <Title entity={person} entityType={EntityType.PERSON} /> : <br />}
      visible
      zIndex={zIndex}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Content
          formChanged={detailsFormChanged}
          isMapMode={Boolean(isMapMode)}
          locationEditComponent={locationEditComponent}
          onEnterPinLocationMode={onEnterPinLocationMode}
          onFormChange={setDetailsFormChanged}
          onHide={onHide}
          onNotesChange={setNotesChanged}
          recordData={recordData}
        />
      )}
    </ResizableDrawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isLoading(state),
  recordData: getRecordData(state),
});

const mapDispatchToProps = {
  changeActivitiesFilter,
  fetchPreviewData: fetchPreviewData.request,
  setHasChangesFlag,
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonRecordPane);
